import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Cloud, Activity, Youtube, ExternalLink, Copy, Check } from "lucide-react";

const RELAYS = [
  {
    name: "Castr",
    url: "https://castr.com",
    price: "from ~$25/mo",
    why: "Relay + multistream + monitoring. Castr can't render a web page itself — pair it with the Cloud Encoder (Path A) above, which publishes RTMP into Castr.",
    steps: [
      "Livestream → your stream → Source Setup → choose 'Publish' (not Pull) and copy the RTMP URL + Stream Key.",
      "Paste both into the Cloud Encoder card above and Save, then run the installer on your VPS.",
      "Destinations → Add destination → YouTube (connect Google) — toggle ON.",
      "Optional: Backup tab → copy the backup ingest for failover; Monitoring tab shows bitrate/health.",
    ],
  },
  {
    name: "OneStream Live",
    url: "https://onestream.live",
    price: "from ~$45/mo (Embed Player / Hosted Live)",
    why: "Simple UI; 'Embed Player' plan can loop a web page or playlist to YouTube nonstop.",
    steps: [
      "Sign up → Hosted Live / Embed Player → New Stream.",
      "Choose 'Web URL' as source and paste the Capture URL. Set resolution 1080p.",
      "Add YouTube as destination via Connect Account. Enable 'Loop / Continuous'.",
      "Schedule as 'Start now' with no end date, then Go Live.",
    ],
  },
  {
    name: "Restream Studio",
    url: "https://restream.io",
    price: "from ~$16–41/mo",
    why: "Great multistream; browser-based Studio can run from any always-on browser, but needs a browser tab open somewhere (use with a cloud desktop if chosen).",
    steps: [
      "Restream → Studio → Add source → 'Website / Browser' (paste Capture URL).",
      "Connect YouTube under Destinations. Set 1080p 30fps.",
      "Because Studio runs in your browser, host it on a cloud desktop (e.g. a $10 Windows VPS) if you want true 24/7.",
    ],
  },
];

function Copyable({ value, testId }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => navigator.clipboard?.writeText(value).then(() => { setOk(true); setTimeout(() => setOk(false), 1500); })}
      className="inline-flex max-w-full items-center gap-2 rounded-sm border border-white/15 bg-black/40 px-3 py-1.5 font-mono text-[11px] text-gray-200 hover:border-[#1f63c9]"
    >
      <span className="truncate">{value}</span>
      {ok ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

export function StreamHealthWidget() {
  const [h, setH] = useState(null);
  useEffect(() => {
    const load = () => api.get("/broadcast/health").then((r) => setH(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  if (!h) return null;
  const enc = h.encoder, yt = h.youtube;
  const pill = (ok, onText, offText, unknownText) => (
    <span className={`rounded-full px-3 py-1 font-oswald text-xs font-bold uppercase tracking-widest ${ok === true ? "bg-green-500/20 text-green-300" : ok === false ? "bg-red-500/20 text-red-300" : "bg-white/10 text-gray-300"}`}>
      {ok === true ? onText : ok === false ? offText : unknownText}
    </span>
  );
  return (
    <div className="glass rounded-sm p-5" data-testid="stream-health-widget">
      <div className="mb-3 flex items-center gap-2 font-oswald text-sm font-bold uppercase tracking-widest text-white">
        <Activity className="h-4 w-4 text-[#1f63c9]" /> 24/7 Stream Health
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-sm border border-white/10 bg-black/40 p-3" data-testid="encoder-status">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-white"><Cloud className="h-4 w-4 text-gray-300" /> Cloud browser rendering /broadcast</div>
            {pill(enc.online, "Online", enc.last_seen ? "Offline" : "Never seen", "Unknown")}
          </div>
          <div className="mt-2 font-mono text-[11px] text-gray-400">
            {enc.last_seen ? <>Last heartbeat {enc.age_s}s ago · slide <span className="text-gray-200">{enc.slide || "—"}</span> · {enc.viewport || "?"} · from {enc.ip || "?"}</> : "Open the Capture URL in your relay service; a heartbeat arrives within 30 seconds."}
          </div>
          {enc.viewport && enc.viewport !== "1920x1080" && enc.online && (
            <div className="mt-1 text-[11px] text-amber-300">Viewport is {enc.viewport} — set the relay's browser source to 1920×1080 for a pixel-perfect feed.</div>
          )}
        </div>
        <div className="rounded-sm border border-white/10 bg-black/40 p-3" data-testid="youtube-status">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-white"><Youtube className="h-4 w-4 text-red-400" /> YouTube live stream</div>
            {yt.video_id ? pill(yt.live, "Live now", "Not live", "Checking…") : pill(null, "", "", "No video ID set")}
          </div>
          <div className="mt-2 font-mono text-[11px] text-gray-400">
            {yt.video_id ? <>{yt.title || yt.video_id}{yt.owner ? ` · ${yt.owner}` : ""}</> : "Save your live stream URL in Live Stream Settings so we can watch it."}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CloudRelayGuide({ captureUrl }) {
  return (
    <div className="glass rounded-sm border border-[#1f63c9]/40 p-6" data-testid="cloud-relay-guide">
      <div className="flex items-center gap-2">
        <Cloud className="h-5 w-5 text-[#1f63c9]" />
        <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Option 2 · Managed Relays (Castr & friends)</div>
      </div>
      <p className="mt-1 text-sm text-gray-300">
        Relay services push video to YouTube 24/7 and handle reconnects, but most <b>cannot render a web page by themselves</b> — their "browser source" runs in your own browser tab. Use them together with the Cloud Encoder above. If a service does offer a true server-side Web Page source, give it this Capture URL:
      </p>
      <div className="mt-3"><Copyable value={captureUrl} testId="cloud-capture-url" /></div>
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        {RELAYS.map((r) => (
          <div key={r.name} className="rounded-sm border border-white/10 bg-black/35 p-4" data-testid={`relay-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <div className="font-oswald text-base font-bold uppercase tracking-wider text-white">{r.name}</div>
              <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#ffd60a] hover:underline">Open <ExternalLink className="h-3 w-3" /></a>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{r.price}</div>
            <p className="mt-2 text-xs text-gray-300">{r.why}</p>
            <ol className="mt-3 space-y-1.5 text-xs text-gray-200">
              {r.steps.map((st, i) => <li key={i} className="flex gap-2"><span className="font-oswald font-bold text-[#1f63c9]">{i + 1}</span><span>{st}</span></li>)}
            </ol>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-sm border border-white/10 bg-black/35 p-4 text-xs text-gray-300">
        <div className="mb-1 font-oswald text-xs font-bold uppercase tracking-widest text-white">YouTube Studio settings (once)</div>
        <ul className="list-disc space-y-1 pl-4">
          <li>Go Live → Stream → use the <b>default persistent stream key</b> (not a one-time event).</li>
          <li>Turn <b>Enable Auto-start</b> ON and <b>Enable Auto-stop</b> OFF so the relay can reconnect after any hiccup.</li>
          <li>Latency: <b>Normal</b>. Resolution 1080p30, 4,500–6,000 kbps. Then paste the resulting live URL into <b>Live Stream Settings</b> so the health check above can watch it.</li>
        </ul>
      </div>
    </div>
  );
}
